import type { ResumeData } from "@/types/resume";
import { Mail, Phone, MapPin, Globe, Linkedin, Github } from "lucide-react";
import { formatDate, getOrderedSections } from "./templateUtils";
import { bodyFont, headingFont } from "./useResumeFonts";

export function TemplateModernProfessional({ data, overridePrimary }: { data: ResumeData; overridePrimary?: string }) {
  const { personalInfo: p } = data;
  const PRIMARY = overridePrimary || "#1e40af";
  const PRIMARY_LIGHT = overridePrimary || "#3b82f6";
  const PRIMARY_TINT = "#dbeafe";
  const FONT_BODY = bodyFont(data, "'Segoe UI', system-ui, sans-serif");
  const FONT_HEADING = headingFont(data, FONT_BODY);

  const sectionRenderers: Record<string, React.ReactNode> = {
    summary: data.summary ? <Section color={PRIMARY} tint={PRIMARY_TINT} key="summary" title="Summary"><p>{data.summary}</p></Section> : null,
    workExperience: data.workExperience.length > 0 ? (
      <Section color={PRIMARY} tint={PRIMARY_TINT} key="workExperience" title="Experience">
        {data.workExperience.map((exp) => (
          <div key={exp.id} className="mb-3 last:mb-0">
            <div className="flex justify-between items-start">
              <div><strong style={{ color: PRIMARY }}>{exp.jobTitle}</strong>{exp.company && <span className="font-medium"> | {exp.company}</span>}</div>
              <span className="text-[10px] whitespace-nowrap" style={{ color: "#475569" }}>{exp.startDate && formatDate(exp.startDate)} — {exp.currentlyWorking ? "Present" : exp.endDate ? formatDate(exp.endDate) : ""}</span>
            </div>
            {exp.location && <p className="text-[10px]" style={{ color: "#475569" }}>{exp.location}</p>}
            {exp.responsibilities.filter(Boolean).length > 0 && <ul className="list-disc ml-4 mt-1 space-y-0.5">{exp.responsibilities.filter(Boolean).map((r, i) => <li key={i}>{r}</li>)}</ul>}
          </div>
        ))}
      </Section>
    ) : null,
    education: data.education.length > 0 ? (
      <Section color={PRIMARY} tint={PRIMARY_TINT} key="education" title="Education">
        {data.education.map((edu) => (
          <div key={edu.id} className="mb-2 last:mb-0">
            <div className="flex justify-between"><div><strong>{edu.degree}</strong>{edu.institution && <span> — {edu.institution}</span>}</div>{edu.graduationDate && <span className="text-[10px]" style={{ color: "#475569" }}>{formatDate(edu.graduationDate)}</span>}</div>
            {edu.location && <p className="text-[10px]" style={{ color: "#475569" }}>{edu.location}</p>}
            {edu.gpa && <p>GPA: {edu.gpa}</p>}
          </div>
        ))}
      </Section>
    ) : null,
    skills: data.skills.length > 0 ? (
      <Section color={PRIMARY} tint={PRIMARY_TINT} key="skills" title="Skills">
        {data.skills.map((g) => <div key={g.id} className="mb-1 last:mb-0">{g.category && <strong style={{ color: PRIMARY }}>{g.category}: </strong>}<span>{g.skills.filter(Boolean).join(", ")}</span></div>)}
      </Section>
    ) : null,
    projects: data.projects.length > 0 ? <Section color={PRIMARY} tint={PRIMARY_TINT} key="projects" title="Projects">{data.projects.map((p) => <div key={p.id} className="mb-2 last:mb-0"><strong>{p.title}</strong>{p.role && <span> — {p.role}</span>}{p.description && <p className="mt-0.5">{p.description}</p>}</div>)}</Section> : null,
    certifications: data.certifications.length > 0 ? <Section color={PRIMARY} tint={PRIMARY_TINT} key="certifications" title="Certifications">{data.certifications.map((c) => <div key={c.id} className="mb-1 last:mb-0"><strong>{c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: PRIMARY }}>{c.name}</a> : c.name}</strong>{c.issuer && <span> — {c.issuer}</span>}{c.issueDate && <span className="text-[10px] ml-2" style={{ color: "#475569" }}>{formatDate(c.issueDate)}</span>}</div>)}</Section> : null,
    languages: data.languages.length > 0 ? <Section color={PRIMARY} tint={PRIMARY_TINT} key="languages" title="Languages"><p>{data.languages.map((l) => `${l.language} (${l.proficiency})`).join(", ")}</p></Section> : null,
    achievements: data.achievements.filter(Boolean).length > 0 ? <Section color={PRIMARY} tint={PRIMARY_TINT} key="achievements" title="Achievements"><ul className="list-disc ml-4 space-y-0.5">{data.achievements.filter(Boolean).map((a, i) => <li key={i}>{a}</li>)}</ul></Section> : null,
  };

  return (
    <div className="text-xs leading-relaxed" style={{ fontFamily: FONT_BODY, color: "#1e293b", background: "#ffffff" }}>
      <div className="px-6 pt-6 pb-4 flex items-start gap-4" style={{ borderBottom: `3px solid ${PRIMARY_LIGHT}` }}>
        {p.photoUrl && <img src={p.photoUrl} alt="" width={64} height={64} loading="lazy" decoding="async" className="w-16 h-16 rounded-full object-cover shrink-0" style={{ border: `2px solid ${PRIMARY_LIGHT}` }} />}
        <div>
          {p.fullName && <h1 className="text-xl font-bold mb-0.5" style={{ color: PRIMARY, fontFamily: FONT_HEADING }}>{p.fullName}</h1>}
          {p.professionalTitle && <p className="text-sm font-medium" style={{ color: "#475569" }}>{p.professionalTitle}</p>}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[10px]" style={{ color: "#475569" }}>
            {p.email && <span className="flex items-center gap-0.5"><Mail className="w-2.5 h-2.5" />{p.email}</span>}
            {p.phone && <span className="flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{p.phone}</span>}
            {p.city && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{p.city}{p.country ? `, ${p.country}` : ""}</span>}
            {p.linkedin && <span className="flex items-center gap-0.5"><Linkedin className="w-2.5 h-2.5" />{p.linkedin}</span>}
            {p.github && <span className="flex items-center gap-0.5"><Github className="w-2.5 h-2.5" />{p.github}</span>}
            {p.portfolio && <span className="flex items-center gap-0.5"><Globe className="w-2.5 h-2.5" />{p.portfolio}</span>}
          </div>
        </div>
      </div>
      <div className="px-6 py-4 space-y-4">
        {getOrderedSections(data).map((key) => sectionRenderers[key])}
      </div>
    </div>
  );
}

function Section({ title, children, color = "#1e40af", tint = "#dbeafe" }: { title: string; children: React.ReactNode; color?: string; tint?: string }) {
  return (
    <div>
      <h2 className="text-xs font-bold uppercase tracking-wider mb-1.5 pb-1" style={{ color, borderBottom: `1px solid ${tint}` }}>{title}</h2>
      {children}
    </div>
  );
}
